import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TableContainer, TableHead, TableCell, TableRow, TableBody, Table } from '@material-ui/core';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import { EntrepreneurExecutiveSummary } from '../../types/types';
import Form from '../../components/Form';
import { formikSubmitDisabled } from '../../lib/utils';
import { StyledText } from '../../components/StyledText';
import { entPublishedExecSummary } from '../../lib/validationSchemas';
import FinanceTable from '../../components/FinanceTable';

const initialValues = (values: EntrepreneurExecutiveSummary) => ({
  background: values.background || '',
  management: values.management || '',
  market: values.market || '',
  distribution: values.distribution || '',
  competition: values.competition || '',
  financialProjection: values.financialProjection || []
});

export const unpublishedExecValidationSchema = Yup.object().shape({
  background: Yup.string().trim(),
  management: Yup.string().trim(),
  market: Yup.string().trim(),
  distribution: Yup.string().trim(),
  competition: Yup.string().trim(),
  financialProjection: Yup.array().of(
    Yup.object().shape({
      id: Yup.number(),
      year: Yup.number(),
      revenue: Yup.number().nullable(),
      EBITDA: Yup.number().nullable()
    })
  )
});

const getValidationSchema = (isPublished: boolean) => (isPublished ? entPublishedExecSummary : unpublishedExecValidationSchema);

interface TableEditCellProps {
  id: string
  value: number
  formik: any
}

const TableEditCell: React.FC<TableEditCellProps> = ({
  id, value, formik
}) => (
  <TableCell>
    <input
      type='number'
      id={id}
      name={id}
      value={value}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
    />
  </TableCell>
);
interface EntExecSummaryEditProps {
  open: boolean
  handleClose: any
  mainPanelBody: EntrepreneurExecutiveSummary
  isPublished: boolean
  handleSubmit: (values: EntrepreneurExecutiveSummary) => void
}

const bgPlaceholder = 'What is your company going to make? Why is your product critically important to your users? What is new or different about what you are making?';
const mgmtPlaceholder = 'Who is involved in the company, what are their backgrounds, commitment level and ownership stake? What uniquely relevant skills, knowledge or background will make you successful?';
const mktPlaceholder = 'What is the size of the addressable market? How do you know? How much could you capture?';
const distPlaceholder = 'How will you get users? How will you make money?';
const compPlaceholder = 'Who are your competitors and how are you different?';

const EntExecSummaryEdit: React.FC<EntExecSummaryEditProps> = ({
  open,
  handleClose,
  mainPanelBody,
  isPublished,
  handleSubmit
}) => {
  const validationSchema = getValidationSchema(isPublished);
  const formik = useFormik({
    initialValues: initialValues(mainPanelBody),
    validationSchema,
    enableReinitialize: true,
    validateOnBlur: true,
    onSubmit: handleSubmit
  });

  const handleCancel = () => {
    handleClose();
    formik.resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <Form formik={formik}>
        <DialogTitle disableTypography>
          <StyledText variant='h2'>Edit Detailed Info</StyledText>
        </DialogTitle>
        <DialogContent>
          <TextInput
            type='text'
            fullWidth
            id='background'
            label='Background'
            placeholder={bgPlaceholder}
            multiline
            rows={6}
          />
          <TextInput
            type='text'
            fullWidth
            id='management'
            label='Management'
            placeholder={mgmtPlaceholder}
            multiline
            rows={6}
          />
          <TextInput
            type='text'
            fullWidth
            id='market'
            label='Market'
            placeholder={mktPlaceholder}
            multiline
            rows={6}
          />
          <TextInput
            type='text'
            fullWidth
            id='distribution'
            label='Distribution Channels'
            placeholder={distPlaceholder}
            multiline
            rows={6}
          />
          <TextInput
            type='text'
            fullWidth
            id='competition'
            label='Competition'
            placeholder={compPlaceholder}
            multiline
            rows={6}
          />
          <StyledText variant='h6' style={{ color: '#5C657C', marginTop: 20 }}>Financial Projections</StyledText>
          <StyledText color='textSecondary' style={{ marginBottom: 20 }}>unaudited, in dollars</StyledText>
          <FinanceTable projections={formik.values.financialProjection} vertical editable />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel}
            type='button'
            variant='contained'
            color='secondary'
          >
            Cancel
          </Button>
          {formik.isSubmitting ? <CircularProgress />
            : (
              <Button
                type='submit'
                variant='contained'
                disableElevation
                color='primary'
                size='large'
                disabled={formikSubmitDisabled(formik)}
              >
                Update
              </Button>
            )}
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default EntExecSummaryEdit;
